import { useState } from 'react'
import { Info, Plus } from 'lucide-react'
import { EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useClearanceTemplate, useExitMutations, useNoticeRules } from '../api'
import { CLEARANCE_AREA_LABEL } from '../types'
import type { ClearanceArea } from '../types'

/** Notice periods and the checklist every case starts with. Neither ships with anything. */
export function ExitSettingsPage() {
  useBreadcrumb()
  const rules = useNoticeRules()
  const template = useClearanceTemplate()
  const actions = useExitMutations()

  const [rule, setRule] = useState<{
    id?: string; contractType: string; minServiceMonths: string; noticeDays: string
    appliesTo: string; paymentInLieuAllowed: boolean; notes: string
  } | null>(null)
  const [item, setItem] = useState<{
    id?: string; area: string; name: string; description: string; required: boolean
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Exit settings"
        subtitle="What notice is owed, and who signs somebody out."
      />

      <p className="flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-3 text-sm text-[var(--color-body)]">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Nothing here is seeded. A notice period is a contract term and a statute, and which
        departments sign somebody out is a fact about how this company is organised — so with
        no rule configured a case asks for the last working day directly and says that nothing
        computed it.
      </p>

      <Card>
        <div className="flex items-center justify-between border-b border-[var(--color-border)] p-3">
          <h3 className="text-sm font-semibold text-[var(--color-heading)]">Notice periods</h3>
          <Button
            type="button" size="sm"
            onClick={() => {
              setError(null)
              setRule({
                contractType: '', minServiceMonths: '0', noticeDays: '', appliesTo: 'BOTH',
                paymentInLieuAllowed: false, notes: '',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New rule
          </Button>
        </div>
        {rules.isPending && <Skeleton className="m-4 h-16" />}
        {rules.isError && <ErrorState error={rules.error} onRetry={() => void rules.refetch()} />}
        {rules.isSuccess && rules.data.length === 0 && (
          <EmptyState
            title="No notice period is configured"
            description="Until one is, nothing computes a leaving date and every case sets it by hand — which is the honest answer, not a broken one."
          />
        )}
        {rules.isSuccess && rules.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
              <tr>
                <th className="p-3 text-start font-medium">Contract</th>
                <th className="p-3 text-start font-medium">From service of</th>
                <th className="p-3 text-start font-medium">Notice</th>
                <th className="p-3 text-start font-medium">Applies to</th>
                <th className="p-3 text-start font-medium">In lieu</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {rules.data.map((row) => (
                <tr key={row.id} className="border-b border-[var(--color-border)]">
                  <td className="p-3">{row.contractType ?? 'Any'}</td>
                  <td className="p-3">{row.minServiceMonths} month(s)</td>
                  <td className="p-3">{row.noticeDays} day(s)</td>
                  <td className="p-3">{row.appliesTo.toLowerCase()}</td>
                  <td className="p-3">{row.paymentInLieuAllowed ? 'Allowed' : 'No'}</td>
                  <td className="p-3 text-end">
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => {
                        setError(null)
                        setRule({
                          id: row.id,
                          contractType: row.contractType ?? '',
                          minServiceMonths: String(row.minServiceMonths),
                          noticeDays: String(row.noticeDays),
                          appliesTo: row.appliesTo,
                          paymentInLieuAllowed: row.paymentInLieuAllowed,
                          notes: row.notes ?? '',
                        })
                      }}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card>
        <div className="flex items-center justify-between border-b border-[var(--color-border)] p-3">
          <h3 className="text-sm font-semibold text-[var(--color-heading)]">Clearance checklist</h3>
          <Button
            type="button" size="sm"
            onClick={() => {
              setError(null)
              setItem({ area: 'IT', name: '', description: '', required: true })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New line
          </Button>
        </div>
        {template.isPending && <Skeleton className="m-4 h-16" />}
        {template.isSuccess && template.data.length === 0 && (
          <EmptyState
            title="No template line"
            description="Assets, loans, documents, tickets and access are added to every case automatically. Anything else your departments sign off belongs here."
          />
        )}
        {template.isSuccess && template.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
              <tr>
                <th className="p-3 text-start font-medium">Area</th>
                <th className="p-3 text-start font-medium">What has to happen</th>
                <th className="p-3 text-start font-medium">Required</th>
                <th className="p-3 text-start font-medium">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {template.data.map((row) => (
                <tr key={row.id} className="border-b border-[var(--color-border)]">
                  <td className="p-3">
                    {CLEARANCE_AREA_LABEL[row.area as ClearanceArea] ?? row.area}
                  </td>
                  <td className="p-3">
                    {row.name}
                    {row.description && (
                      <span className="block text-xs text-[var(--color-muted)]">
                        {row.description}
                      </span>
                    )}
                  </td>
                  <td className="p-3">{row.required ? 'Yes' : 'Optional'}</td>
                  <td className="p-3">
                    <StatusBadge
                      status={row.active ? 'ACTIVE' : 'INACTIVE'}
                      tone={row.active ? 'success' : 'neutral'}
                      label={row.active ? 'Active' : 'Off'}
                    />
                  </td>
                  <td className="p-3 text-end">
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => {
                        setError(null)
                        setItem({
                          id: row.id, area: row.area, name: row.name,
                          description: row.description ?? '', required: row.required,
                        })
                      }}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <FormDialog
        open={rule != null}
        onOpenChange={(open) => { if (!open) { setRule(null); setError(null) } }}
        title={rule?.id ? 'Edit notice rule' : 'New notice rule'}
        submitLabel="Save"
        loading={actions.saveRule.isPending}
        error={error}
        onSubmit={() => {
          if (!rule) return
          void (async () => {
            setError(null)
            try {
              await actions.saveRule.mutateAsync({
                id: rule.id,
                legalEntityId: null,
                contractType: rule.contractType || null,
                minServiceMonths: Number(rule.minServiceMonths || 0),
                noticeDays: rule.noticeDays === '' ? null : Number(rule.noticeDays),
                paymentInLieuAllowed: rule.paymentInLieuAllowed,
                appliesTo: rule.appliesTo,
                notes: rule.notes || null,
                active: true,
              })
              setRule(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That rule could not be saved')
            }
          })()
        }}
      >
        {rule && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="rule-contract">Contract type</Label>
              <Input
                id="rule-contract" value={rule.contractType} placeholder="Any"
                onChange={(event) => setRule({ ...rule, contractType: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="rule-months">From service of (months)</Label>
              <Input
                id="rule-months" type="number" value={rule.minServiceMonths}
                onChange={(event) => setRule({ ...rule, minServiceMonths: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="rule-days">Notice days</Label>
              <Input
                id="rule-days" type="number" value={rule.noticeDays}
                onChange={(event) => setRule({ ...rule, noticeDays: event.target.value })}
              />
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                There is no default. This number comes from the contract and the statute.
              </p>
            </div>
            <div>
              <Label htmlFor="rule-applies">Applies to</Label>
              <Select
                id="rule-applies" className="w-full" value={rule.appliesTo}
                onChange={(event) => setRule({ ...rule, appliesTo: event.target.value })}
              >
                <option value="BOTH">Both</option>
                <option value="RESIGNATION">Resignation</option>
                <option value="TERMINATION">Termination</option>
              </Select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox" className="size-4" checked={rule.paymentInLieuAllowed}
                  onChange={(event) =>
                    setRule({ ...rule, paymentInLieuAllowed: event.target.checked })}
                />
                Payment in lieu allowed
              </label>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="rule-notes">Notes</Label>
              <Textarea
                id="rule-notes" value={rule.notes}
                onChange={(event) => setRule({ ...rule, notes: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={item != null}
        onOpenChange={(open) => { if (!open) { setItem(null); setError(null) } }}
        title={item?.id ? 'Edit checklist line' : 'New checklist line'}
        submitLabel="Save"
        loading={actions.saveTemplateItem.isPending}
        error={error}
        onSubmit={() => {
          if (!item) return
          void (async () => {
            setError(null)
            try {
              await actions.saveTemplateItem.mutateAsync({
                id: item.id,
                legalEntityId: null,
                area: item.area,
                name: item.name,
                description: item.description || null,
                ownerRoleId: null,
                required: item.required,
                sortOrder: 100,
                active: true,
              })
              setItem(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That line could not be saved')
            }
          })()
        }}
      >
        {item && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="template-area">Area</Label>
              <Select
                id="template-area" className="w-full" value={item.area}
                onChange={(event) => setItem({ ...item, area: event.target.value })}
              >
                {Object.entries(CLEARANCE_AREA_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="template-name">What has to happen</Label>
              <Input
                id="template-name" value={item.name}
                onChange={(event) => setItem({ ...item, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="template-description">Detail</Label>
              <Textarea
                id="template-description" value={item.description}
                onChange={(event) => setItem({ ...item, description: event.target.value })}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox" className="size-4" checked={item.required}
                onChange={(event) => setItem({ ...item, required: event.target.checked })}
              />
              The settlement waits for this one
            </label>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
